import { useEffect, useRef, useState } from 'react';
import { apiPost, ApiError } from '../lib/api';
import { Modal, Button, Alert, Badge } from '../components/ui';
import { parseCsv, IMPORT_FIELDS, guessField, ImportField } from '../lib/csv';

// ── Types ──────────────────────────────────────────────────────────────────

type Step = 'upload' | 'map' | 'review' | 'commit';

type Mapping = Record<ImportField, number>;

type MappedRow = Partial<Record<ImportField, string>>;

type ConflictEntry = {
  index: number;
  incoming: { businessName?: string; contactName?: string; email?: string; phone?: string };
  existing: { id: string; businessName?: string; contactName?: string; email?: string; phone?: string };
};

type PreviewResult = {
  total: number;
  newCount: number;
  conflicts: ConflictEntry[];
};

type CommitResult = {
  inserted: number;
  updated: number;
  skipped: number;
};

type Resolution = 'keep' | 'overwrite' | 'merge';

// ── Helpers ────────────────────────────────────────────────────────────────

function buildInitialMapping(headers: string[]): Mapping {
  const mapping = {} as Mapping;
  for (const { key } of IMPORT_FIELDS) {
    const idx = headers.findIndex((h) => guessField(h) === key);
    mapping[key] = idx >= 0 ? idx : -1;
  }
  return mapping;
}

function buildMappedRows(rows: string[][], mapping: Mapping): MappedRow[] {
  return rows.map((row) => {
    const obj: MappedRow = {};
    for (const { key } of IMPORT_FIELDS) {
      const idx = mapping[key];
      if (idx >= 0) {
        obj[key] = row[idx] ?? '';
      }
    }
    return obj;
  });
}

function countResolved(conflicts: ConflictEntry[], resolutions: Record<number, Resolution>): number {
  return conflicts.filter((c) => (resolutions[c.index] ?? 'keep') !== 'keep').length;
}

function displayName(row: { businessName?: string; contactName?: string }): string {
  return row.businessName || row.contactName || '—';
}

// ── Component ──────────────────────────────────────────────────────────────

export function ImportContacts({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string>('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [uploadError, setUploadError] = useState<string>('');
  const [mapping, setMapping] = useState<Mapping>({} as Mapping);
  const [mapError, setMapError] = useState<string>('');
  const [mappedRows, setMappedRows] = useState<MappedRow[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string>('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [resolutions, setResolutions] = useState<Record<number, Resolution>>({});
  const [commitLoading, setCommitLoading] = useState(false);
  const [commitError, setCommitError] = useState<string>('');
  const [commitResult, setCommitResult] = useState<CommitResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Reset all state when modal opens
  useEffect(() => {
    if (open) {
      setStep('upload');
      setFileName('');
      setHeaders([]);
      setRows([]);
      setUploadError('');
      setMapping({} as Mapping);
      setMapError('');
      setMappedRows([]);
      setPreviewLoading(false);
      setPreviewError('');
      setPreview(null);
      setResolutions({});
      setCommitLoading(false);
      setCommitError('');
      setCommitResult(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [open]);

  // ── Step 1: Upload ───────────────────────────────────────────────────────

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setFileName(file.name);
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.rows.length === 0) {
      setUploadError('No data rows found in this file. Please check the CSV and try again.');
      setHeaders([]);
      setRows([]);
      return;
    }
    setHeaders(parsed.headers);
    setRows(parsed.rows);
  }

  function handleGoToMap() {
    const m = buildInitialMapping(headers);
    setMapping(m);
    setMapError('');
    setStep('map');
  }

  // ── Step 2: Map ──────────────────────────────────────────────────────────

  function setFieldMapping(key: ImportField, idx: number) {
    setMapping((prev) => ({ ...prev, [key]: idx }));
  }

  const mappedFieldsForPreview = IMPORT_FIELDS.filter(({ key }) => mapping[key] >= 0);

  async function handleGoToReview() {
    const requiredKeys: ImportField[] = ['email', 'phone', 'businessName', 'contactName'];
    const hasRequired = requiredKeys.some((k) => mapping[k] >= 0);
    if (!hasRequired) {
      setMapError('Map at least one of: Email, Phone, Business name, or Person name to continue.');
      return;
    }
    setMapError('');
    const built = buildMappedRows(rows, mapping);
    setMappedRows(built);
    setPreviewLoading(true);
    setPreviewError('');
    setStep('review');
    try {
      const result = await apiPost<PreviewResult>('/api/contacts/import', {
        rows: built,
        commit: false,
      });
      setPreview(result);
      // Default all conflicts to 'keep'
      const defaults: Record<number, Resolution> = {};
      for (const c of result.conflicts) {
        defaults[c.index] = 'keep';
      }
      setResolutions(defaults);
    } catch (err) {
      setPreviewError(err instanceof ApiError ? err.message : 'Preview failed. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  }

  // ── Step 3: Review ───────────────────────────────────────────────────────

  function setResolution(index: number, value: Resolution) {
    setResolutions((prev) => ({ ...prev, [index]: value }));
  }

  async function handleCommit() {
    if (!preview) return;
    setCommitLoading(true);
    setCommitError('');
    try {
      const result = await apiPost<CommitResult>('/api/contacts/import', {
        rows: mappedRows,
        commit: true,
        resolutions,
        batchName: fileName || 'Imported list',
      });
      setCommitResult(result);
      setStep('commit');
    } catch (err) {
      setCommitError(err instanceof ApiError ? err.message : 'Import failed. Please try again.');
    } finally {
      setCommitLoading(false);
    }
  }

  const importCount = preview
    ? preview.newCount + countResolved(preview.conflicts, resolutions)
    : 0;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Modal open={open} onClose={onClose} title="Import contacts">
      {/* STEP 1: Upload */}
      {step === 'upload' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            Upload a CSV file to import contacts. The first row must be column headers.
          </p>
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed border-line p-6 transition hover:border-electric">
            <span className="text-sm font-semibold text-soft">Choose a CSV file</span>
            <span className="text-xs text-muted">or drag and drop here</span>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>
          {uploadError && <Alert kind="error">{uploadError}</Alert>}
          {rows.length > 0 && !uploadError && (
            <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2">
              <Badge className="border-electric/40 bg-electric/10 text-electric">
                {headers.length} columns
              </Badge>
              <Badge className="border-success/40 bg-success/10 text-success">
                {rows.length} rows
              </Badge>
              <span className="ml-1 text-xs text-muted">detected</span>
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={rows.length === 0}
              onClick={handleGoToMap}
            >
              Next: map columns
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2: Map */}
      {step === 'map' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            Match your CSV columns to contact fields.
          </p>
          <div className="flex flex-col gap-2">
            {IMPORT_FIELDS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-sm text-soft">{label}</span>
                <select
                  className="input flex-1"
                  value={mapping[key] ?? -1}
                  onChange={(e) => setFieldMapping(key, Number(e.target.value))}
                >
                  <option value={-1}>— skip —</option>
                  {headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {/* Preview table */}
          {mappedFieldsForPreview.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-line bg-surface-2">
                    {mappedFieldsForPreview.map(({ key, label }) => (
                      <th key={key} className="px-2 py-1.5 text-left font-semibold text-muted">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 3).map((row, ri) => (
                    <tr key={ri} className="border-b border-line last:border-0">
                      {mappedFieldsForPreview.map(({ key }) => (
                        <td key={key} className="truncate px-2 py-1.5 text-soft" style={{ maxWidth: 120 }}>
                          {row[mapping[key]] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {mapError && <Alert kind="error">{mapError}</Alert>}
          <div className="flex justify-between gap-2 border-t border-line pt-4">
            <Button variant="ghost" onClick={() => setStep('upload')}>
              Back
            </Button>
            <Button variant="primary" onClick={handleGoToReview}>
              Next: check for duplicates
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3: Review */}
      {step === 'review' && (
        <div className="flex flex-col gap-4">
          {previewLoading && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted">
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-90" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
              Checking for duplicates…
            </div>
          )}
          {previewError && <Alert kind="error">{previewError}</Alert>}
          {preview && !previewLoading && (
            <>
              <div className="flex items-center gap-3">
                <Badge className="border-success/40 bg-success/10 text-success">
                  {preview.newCount} new
                </Badge>
                <Badge className="border-electric/40 bg-electric/10 text-electric">
                  {preview.conflicts.length} existing
                </Badge>
                <span className="text-xs text-muted">out of {preview.total} rows</span>
              </div>

              {preview.conflicts.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                    Resolve conflicts
                  </p>
                  <div className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-1">
                    {preview.conflicts.map((c) => (
                      <div key={c.index} className="card rounded-lg p-3">
                        <div className="mb-2 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="mb-1 block font-semibold text-electric">Incoming</span>
                            <div className="text-soft">{displayName(c.incoming)}</div>
                            <div className="text-muted">{c.incoming.email || '—'}</div>
                            <div className="text-muted">{c.incoming.phone || '—'}</div>
                          </div>
                          <div>
                            <span className="mb-1 block font-semibold text-muted">Existing</span>
                            <div className="text-soft">{displayName(c.existing)}</div>
                            <div className="text-muted">{c.existing.email || '—'}</div>
                            <div className="text-muted">{c.existing.phone || '—'}</div>
                          </div>
                        </div>
                        <div className="flex gap-4 text-xs">
                          {(['keep', 'overwrite', 'merge'] as const).map((opt) => (
                            <label key={opt} className="flex cursor-pointer items-center gap-1 text-soft">
                              <input
                                type="radio"
                                name={`res-${c.index}`}
                                value={opt}
                                checked={(resolutions[c.index] ?? 'keep') === opt}
                                onChange={() => setResolution(c.index, opt)}
                                className="accent-electric"
                              />
                              {opt.charAt(0).toUpperCase() + opt.slice(1)}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {commitError && <Alert kind="error">{commitError}</Alert>}
            </>
          )}
          <div className="flex justify-between gap-2 border-t border-line pt-4">
            <Button variant="ghost" onClick={() => setStep('map')} disabled={previewLoading || commitLoading}>
              Back
            </Button>
            <Button
              variant="primary"
              disabled={previewLoading || !preview}
              loading={commitLoading}
              onClick={handleCommit}
            >
              Import {importCount > 0 ? importCount : ''}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 4: Done */}
      {step === 'commit' && commitResult && (
        <div className="flex flex-col gap-4">
          <Alert kind="success">
            Imported {commitResult.inserted} new, updated {commitResult.updated}, skipped{' '}
            {commitResult.skipped}.
          </Alert>
          <div className="flex justify-end border-t border-line pt-4">
            <Button variant="primary" onClick={onDone}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
